import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Car, Mail, Lock, ArrowLeft, Phone, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, API } from "@/App";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [authMode, setAuthMode] = useState("email"); // "email" or "phone"

  // Email state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Phone OTP state
  const [phone, setPhone] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [mockOtp, setMockOtp] = useState(null);
  const [otpLoading, setOtpLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get("oauth_error");
    if (oauthError) {
      toast.error(oauthError);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      login(response.data.user);
      toast.success("Welcome back!");
      
      const role = response.data.user.role;
      if (role === "admin") navigate("/admin");
      else if (role === "valet_staff") navigate("/valet");
      else navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!phone || phone.length < 7) {
      toast.error("Please enter a valid phone number");
      return;
    }

    setOtpLoading(true);
    try {
      const response = await axios.post(`${API}/auth/phone/send-otp`, { phone });
      setOtpSent(true);
      
      if (response.data.mock_otp) {
        setMockOtp(response.data.mock_otp);
        toast.success(`OTP generated! Use: ${response.data.mock_otp}`, { duration: 10000 });
      } else {
        toast.success("OTP sent to your phone!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 6) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }

    setOtpLoading(true);
    try {
      const response = await axios.post(`${API}/auth/phone/verify-otp`, { phone, otp }, { withCredentials: true });
      login(response.data.user);
      
      if (response.data.is_new_user) {
        toast.success("Account created! Welcome to VeloPark!");
      } else {
        toast.success("Welcome back!");
      }

      const role = response.data.user.role;
      if (role === "admin") navigate("/admin");
      else if (role === "valet_staff") navigate("/valet");
      else navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "OTP verification failed");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.assign(`${API}/auth/google`);
  };

  return (
    <div className="min-h-screen bg-[#090A0F] flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 xl:px-24">
        <div className="max-w-md w-full mx-auto">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-white mb-8 transition-colors"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <img src="/logo.svg" alt="VeloPark" className="w-12 h-12 rounded-full" />
            <span className="font-display text-2xl font-semibold text-white">VeloPark</span>
          </div>

          <h1 className="font-display text-3xl font-semibold text-white mb-2">Welcome back</h1>
          <p className="text-[#A1A1AA] mb-8">Sign in to your account to continue</p>

          {/* Google Login */}
          <Button
            variant="outline"
            className="w-full py-6 mb-6 bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white rounded-xl flex items-center justify-center gap-3"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#090A0F] text-[#A1A1AA]">or continue with</span>
            </div>
          </div>

          {/* Auth Mode Toggle */}
          <div className="flex bg-white/5 border border-white/10 rounded-xl p-1 mb-6">
            <button
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                authMode === "email" ? "bg-[#D4AF37] text-[#090A0F]" : "text-[#A1A1AA] hover:text-white"
              }`}
              onClick={() => { setAuthMode("email"); setOtpSent(false); setMockOtp(null); }}
              data-testid="email-tab-btn"
            >
              <Mail className="w-4 h-4" /> Email
            </button>
            <button
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                authMode === "phone" ? "bg-[#D4AF37] text-[#090A0F]" : "text-[#A1A1AA] hover:text-white"
              }`}
              onClick={() => setAuthMode("phone")}
              data-testid="phone-tab-btn"
            >
              <Phone className="w-4 h-4" /> Phone
            </button>
          </div>

          {/* Email Login Form */}
          {authMode === "email" && (
            <form onSubmit={handleEmailLogin} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-11 py-6 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-[#A1A1AA]"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-white">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-11 py-6 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-[#A1A1AA]"
                    data-testid="password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full py-6 btn-primary rounded-xl text-base font-medium"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
          )}

          {/* Phone OTP Login Form */}
          {authMode === "phone" && (
            <div className="space-y-5">
              {!otpSent ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-white">Phone Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+91 98765 43210"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="pl-11 py-6 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-[#A1A1AA]"
                        data-testid="phone-input"
                      />
                    </div>
                    <p className="text-xs text-[#A1A1AA]">Include country code (e.g., +91, +1)</p>
                  </div>

                  <Button
                    type="button"
                    className="w-full py-6 btn-primary rounded-xl text-base font-medium"
                    disabled={otpLoading}
                    onClick={handleSendOtp}
                    data-testid="send-otp-btn"
                  >
                    {otpLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending OTP...</>
                    ) : "Send OTP"}
                  </Button>
                </>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-5">
                  <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-xl p-3 text-center">
                    <p className="text-sm text-[#D4AF37]">OTP sent to <span className="font-medium text-white">{phone}</span></p>
                    {mockOtp && (
                      <p className="text-lg font-mono font-bold text-white mt-1 tracking-widest" data-testid="mock-otp-display">
                        {mockOtp}
                      </p>
                    )}
                    {!mockOtp && <p className="text-xs text-[#A1A1AA] mt-1">Check your phone for the code</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="otp" className="text-white">Enter OTP</Label>
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                      <Input
                        id="otp"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="6-digit code"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                        className="pl-11 py-6 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-[#A1A1AA] text-center text-xl tracking-[0.5em] font-mono"
                        data-testid="otp-input"
                        autoFocus
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full py-6 btn-primary rounded-xl text-base font-medium"
                    disabled={otpLoading || otp.length < 6}
                    data-testid="verify-otp-btn"
                  >
                    {otpLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Verifying...</>
                    ) : "Verify & Sign In"}
                  </Button>

                  <button
                    type="button"
                    className="w-full text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                    onClick={() => { setOtpSent(false); setOtp(""); setMockOtp(null); }}
                    data-testid="change-phone-btn"
                  >
                    Change phone number or resend OTP
                  </button>
                </form>
              )}
            </div>
          )}

          <p className="text-center text-[#A1A1AA] mt-6">
            Don't have an account?{" "}
            <Link to="/register" className="text-[#D4AF37] hover:underline" data-testid="register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/7c75bd88-236d-4e1a-82bf-77a03477f895/images/f0af81a42367ed4e3b9d62189b22e3e4846b42d77f97fdedb4d1c2fb46f6a3b5.png')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#090A0F] via-[#090A0F]/50 to-transparent" />
      </div>
    </div>
  );
};

export default LoginPage;
